import { ReactElement, useEffect, useState } from "react";
import { BookingState } from '../../modules/booking-state/BookingState';
import { CreateBookingRequest } from '../../modules/entities/Entities';
import { VerifyBookingDetailsPageContext } from './VerifyBookingDetailsPageContext';
import { withPageContext } from '../../modules/contexts/WithPageContext';
import { useViewProvider } from '../../modules/contexts/view-provider-context/ViewProvider';
import { useBrandNavigate } from '../../modules/navigation/UseBrandNavigate';
import { PageRoutes } from '../../modules/navigation/PageRoutes';
import { useCreateBookingRequest } from '../../modules/booking-api/BookingApi';
import { useBookingContext } from '../../modules/booking-state/UseBookingContext';
import { VerifyBookingDetailsPageViewProps } from './VerifyBookingDetailsPageViewProps';
import { Titles } from "../../modules/wizard/Tiltes";
import { Steps } from "../../modules/wizard/Steps";
import { describeRecaptchaFailure, executeRecaptcha, prepareRecaptcha, RecaptchaSupersededError } from "../../recaptcha";
import { reportClientError } from "../../modules/error-reporting/ErrorReporter";

// The action must match between the widget, the token request and the payload the
// backend verifies against — keep it in one place.
const RECAPTCHA_ACTION = 'submit_booking';


function buildCreateBookingRequest({
  registrationNumber,
  serviceRequest,
  customerContact,
  selectedSlot,
  nationalIdNumber,
  passportNumber,
  vehicleDetails,
  selectedDealer,
}: VerifyBookingDetailsPageContext, recaptchaToken: string): CreateBookingRequest {
  return {
    dealerCode: selectedDealer.dealerCode,
    dealerName: selectedDealer.name,
    slot: {
      id: selectedSlot.id,
      date: selectedSlot.date,
    },
    customer: {
      customerId: nationalIdNumber,
      passportNumber,
      registrationNumber: registrationNumber ?? '',
      firstName: customerContact.name,
      lastName: customerContact.surname,
      title: customerContact.title,
      email: customerContact.email,
      mobile: customerContact.phoneNumber,
      preferredContactMethodType: customerContact.preferredContactMethodType
    },
    requirements: {
      service: serviceRequest.serviceType.name,
      serviceNotes: serviceRequest.serviceComments ?? '',
    },
    vehicle: {
      modelCode: vehicleDetails.modelCode,
      modelDescription: vehicleDetails.modelDescription,
      modelYear: vehicleDetails.modelYear,
      brand: vehicleDetails.brand,
      mileage: serviceRequest.mileage,
      vin: vehicleDetails.vin,
      regNo: vehicleDetails.registrationNumber ?? '',
    },
    recaptchaToken,
    recaptchaAction: RECAPTCHA_ACTION,
  };
}

function ContextAccessor({
  vehicleDetails,
  vehicleServiceHistory,
  serviceRequest,
  selectedDealer,
  selectedSlot,
  tcosEstimation,
  registrationNumber,
  customerContact,
  passportNumber,
  nationalIdNumber,
}: BookingState): VerifyBookingDetailsPageContext | null {
  if (
    !(
      selectedSlot &&
      vehicleDetails &&
      vehicleServiceHistory &&
      selectedDealer &&
      customerContact &&
      serviceRequest
    )
  ) {
    return null;
  }

  return {
    vehicleDetails,
    customerContact,
    selectedDealer,
    selectedSlot,
    serviceRequest,
    vehicleServiceHistory,
    registrationNumber,
    tcosEstimation,
    passportNumber,
    nationalIdNumber,
  };
}

export const VerifyBookingDetailsPage = withPageContext(ContextAccessor, ({ pageContext }): ReactElement => {
  const viewProvider = useViewProvider();
  const [bookingState, setBookingContext] = useBookingContext();
  const [createBookingRequest, setCreateBookingRequest] = useState<CreateBookingRequest | null>(null);
  const navigate = useBrandNavigate();
  const [recaptchaError, setRecaptchaError] = useState<string | null>(null);

  useEffect(() => {
    prepareRecaptcha(RECAPTCHA_ACTION).catch((error: unknown) => {
      console.error(error);
      reportClientError({
        message: `reCAPTCHA preparation failed on booking details page: ${error instanceof Error ? error.message : String(error)}`,
        details: error instanceof Error ? error.stack : undefined,
      });
    });
  }, []);

  useEffect(() => {
    document.title = `Home : Service Booking UAT : ${Titles[Steps.ReviewAndSubmit]}`;
  }, []);

  useCreateBookingRequest(createBookingRequest, {
    onSuccess: (createdBooking) => {
      console.log('Booking request created:', createdBooking);
      setBookingContext({
        ...bookingState,
        createdBooking,
      });
      navigate(PageRoutes.SendConfirmationMessage);
    },
    onError: (error) => {
      console.error('Failed to create booking request:', error);
    },
  });

  const onVerifyClick = async (): Promise<void> => {
    try {
      setRecaptchaError(null);

      const token = await executeRecaptcha(RECAPTCHA_ACTION);
      const request = buildCreateBookingRequest(pageContext, token);
      setCreateBookingRequest(request);
    } catch (error) {
      // A superseded attempt means the user restarted verification — the newer attempt owns the outcome.
      if (error instanceof RecaptchaSupersededError) return;

      console.error('reCAPTCHA error:', error);
      reportClientError({
        message: `reCAPTCHA verification failed on booking submit: ${error instanceof Error ? error.message : String(error)}`,
        details: error instanceof Error ? error.stack : undefined,
      });
      setRecaptchaError(describeRecaptchaFailure(error));
    }
  };

  const viewProps: VerifyBookingDetailsPageViewProps = {
    pageContext,
    bookingState,
    onVerifyClick,
    recaptchaError,
  };

  return viewProvider.renderVerifyBookingDetailsPage(viewProps);
});
